import { useMemberships } from "@bodhistreams/api/src";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { A11y, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {
	COLLECTION_LABEL,
	enrichPlansWithAvailability,
	PAID_PLANS_ENABLED,
	PLAN_COLORS,
	planRank,
} from "../../config/pricing";
import "./PricingSection.css";

const CheckIcon = ({ color }) => (
	<svg viewBox="0 0 20 20" fill={color} aria-hidden="true">
		<path
			fillRule="evenodd"
			d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.3a1 1 0 00-1.4-1.4L9 10.6 7.7 9.3a1 1 0 00-1.4 1.4l2 2a1 1 0 001.4 0l4-4z"
			clipRule="evenodd"
		/>
	</svg>
);

const CrossIcon = ({ color }) => (
	<svg viewBox="0 0 20 20" fill={color} aria-hidden="true">
		<path
			fillRule="evenodd"
			d="M10 18a8 8 0 100-16 8 8 0 000 16zm-2.3-10.3a1 1 0 011.4 0L10 8.6l.9-.9a1 1 0 111.4 1.4L11.4 10l.9.9a1 1 0 11-1.4 1.4l-.9-.9-.9.9a1 1 0 11-1.4-1.4l.9-.9-.9-.9a1 1 0 010-1.4z"
			clipRule="evenodd"
		/>
	</svg>
);

const buildPlan = (membership) => {
	const key = membership.name.toLowerCase();
	const isFree = key === "free" || membership.price === 0;
	return {
		id: membership.id,
		key,
		title: membership.name,
		monthly: Number(membership.price),
		yearly: Number(membership.yearly_price || membership.price * 12),
		color: PLAN_COLORS[key] || "#7873f5",
		highlighted: PAID_PLANS_ENABLED && key === "premium",
		features: [
			`${membership.max_simultaneous_devices} device${membership.max_simultaneous_devices > 1 ? "s" : ""}`,
			membership.streaming_hours
				? `${membership.streaming_hours} hrs/month streaming`
				: "Unlimited streaming",
			COLLECTION_LABEL[key] || "Collections",
			isFree ? "No playlist playback" : "Playlist playback",
			isFree ? "No commercial use" : "Commercial use allowed",
		],
		membership,
	};
};

const PricingSection = ({
	selectedPlanKey,
	onSelectPlan,
	yearlyBilling: controlledYearly,
	onToggleBilling,
}) => {
	const navigate = useNavigate();
	const [internalYearly, setInternalYearly] = useState(false);
	const isControlled = controlledYearly !== undefined;
	const yearlyBilling = isControlled ? controlledYearly : internalYearly;

	const { data: membershipsData, isLoading: membershipLoading } =
		useMemberships();
	const memberships = Array.isArray(membershipsData) ? membershipsData : [];

	const toggleBilling = () => {
		const next = !yearlyBilling;
		if (isControlled) onToggleBilling?.(next);
		else setInternalYearly(next);
	};

	const handleSelect = (plan) => {
		if (plan.disabled) return;
		if (onSelectPlan) onSelectPlan(plan.key);
		else navigate(`/pricing?plan=${plan.key}`);
	};

	if (membershipLoading && memberships.length === 0) {
		return (
			<div className="pricing-loading">
				<div className="pricing-loading-spinner" />
			</div>
		);
	}

	const plans = enrichPlansWithAvailability(
		memberships
			.map(buildPlan)
			.sort((a, b) => planRank(a.key) - planRank(b.key)),
	);

	return (
		<section className="pricing-section">
			{PAID_PLANS_ENABLED && (
				<div className="pricing-billing-toggle">
					<button
						type="button"
						className="pricing-switch"
						data-checked={yearlyBilling ? "true" : "false"}
						onClick={toggleBilling}
						aria-label="Toggle yearly billing"
					>
						<span className="pricing-switch-label is-monthly">Monthly</span>
						<span className="pricing-switch-label is-yearly">Yearly</span>
						<span className="pricing-switch-thumb" />
					</button>
					{yearlyBilling && (
						<span className="pricing-save-hint">Save up to 25%</span>
					)}
				</div>
			)}

			<div className="pricing-swiper-wrap">
				<Swiper
					modules={[Navigation, Pagination, A11y]}
					spaceBetween={24}
					slidesPerView={1}
					navigation
					pagination={{ clickable: true, dynamicBullets: true }}
					breakpoints={{
						560: { slidesPerView: 2, spaceBetween: 20 },
						860: { slidesPerView: 3, spaceBetween: 22 },
						1100: { slidesPerView: 4, spaceBetween: 24 },
					}}
					className="pricing-swiper"
				>
					{plans.map((plan) => {
						const price = yearlyBilling ? plan.yearly : plan.monthly;
						const savings = plan.monthly * 12 - plan.yearly;
						const showSavings = yearlyBilling && plan.yearly > 0 && savings > 0;
						const isSelected = selectedPlanKey === plan.key;

						return (
							<SwiperSlide key={plan.id}>
								<button
									type="button"
									onClick={() => handleSelect(plan)}
									disabled={plan.disabled}
									className={[
										"pricing-card",
										plan.highlighted && "is-highlighted",
										isSelected && !plan.disabled && "is-selected",
										plan.disabled && "is-disabled",
									]
										.filter(Boolean)
										.join(" ")}
									style={{ "--plan-color": plan.color }}
									aria-pressed={isSelected && !plan.disabled}
								>
									{plan.disabled && (
										<div className="pricing-badge is-soon-badge">
											Coming Soon
										</div>
									)}
									{!plan.disabled && plan.highlighted && (
										<div className="pricing-badge">Most Popular</div>
									)}
									{!plan.disabled && isSelected && !plan.highlighted && (
										<div className="pricing-badge is-selected-badge">
											Selected
										</div>
									)}

									<h3 className="pricing-title">{plan.title} Plan</h3>

									<div className="pricing-price">
										${price.toFixed(2)}
										<span className="pricing-price-period">
											/{yearlyBilling ? "year" : "mo"}
										</span>
									</div>

									<div className="pricing-save">
										{showSavings ? `Save $${savings.toFixed(2)}/year` : ""}
									</div>

									<ul className="pricing-features">
										{plan.features.map((feature) => {
											const isNegative = feature.startsWith("No ");
											const Icon = isNegative ? CrossIcon : CheckIcon;
											const color = isNegative ? "#ef4444" : plan.color;
											return (
												<li key={feature} className="pricing-feature">
													<span className="pricing-feature-icon">
														<Icon color={color} />
													</span>
													{feature}
												</li>
											);
										})}
									</ul>

									<span className="pricing-card-cta">
										{plan.disabled
											? "Coming Soon"
											: onSelectPlan
												? "Select Plan"
												: "See Details"}
										{!plan.disabled && (
											<span className="pricing-card-cta-arrow">→</span>
										)}
									</span>
								</button>
							</SwiperSlide>
						);
					})}
				</Swiper>
			</div>
		</section>
	);
};

export default PricingSection;
